import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Fire, BookOpen, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
const RESEARCH_CATEGORIES = ["All", "Scaling", "Safety", "Reasoning", "Architecture", "Training", "World Models", "Multimodal"];
import { stripHtml } from "@/lib/stripHtml";

const SORT_OPTIONS = ["date", "citations"];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-3 mb-3">
        <div className="w-9 h-9 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-3 bg-slate-200 rounded w-1/4 mb-2" />
          <div className="h-4 bg-slate-200 rounded w-full" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-2/3" />
    </div>
  );
}

function PaperCard({ paper }) {
  const slug = paper.slug || paper.id;
  const catColors = {
    Scaling:        "bg-blue-50 text-blue-700 border-blue-200",
    Safety:         "bg-emerald-50 text-emerald-700 border-emerald-200",
    Reasoning:      "bg-purple-50 text-purple-700 border-purple-200",
    Architecture:   "bg-indigo-50 text-indigo-700 border-indigo-200",
    Training:       "bg-amber-50 text-amber-700 border-amber-200",
    "World Models": "bg-rose-50 text-rose-700 border-rose-200",
    Multimodal:     "bg-cyan-50 text-cyan-700 border-cyan-200",
    Research:       "bg-slate-50 text-slate-700 border-slate-200",
  };

  const arxivId = paper.arxivId || paper.arxiv_id || "";
  const arxivUrl = paper.url || (arxivId ? `https://arxiv.org/abs/${arxivId}` : null);

  return (
    <Link to={`/trending-ai/research/${slug}`} className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
          <BookOpen size={17} className="text-indigo-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${catColors[paper.category] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {paper.category || "Research"}
            </span>
            <span className="text-[10px] text-slate-400 font-medium">{paper.venue || "arXiv"}</span>
            {(paper.isNew || paper.is_new) && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
            {paper.hot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug">
            {stripHtml(paper.title)}
          </h3>
        </div>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed mb-4">{paper.summary || paper.abstract}</p>

      {paper.tags && paper.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {paper.tags.map((tag) => (
            <span key={tag} className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{tag}</span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-3">
        <div>
          <div className="font-medium text-slate-600">
            {(paper.authors || []).slice(0, 2).join(", ")}{(paper.authors || []).length > 2 ? " et al." : ""}
          </div>
          <div>{stripHtml(paper.institution)}</div>
        </div>
        <div className="text-right">
          {paper.citations > 0 && <div className="font-bold text-slate-700">{paper.citations} citations</div>}
          <div>{paper.publishedAt ? new Date(paper.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</div>
        </div>
      </div>

      {arxivUrl && (
        <div className="mt-3">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600">
            {arxivId ? `arXiv:${arxivId}` : "Read paper"} <ArrowUpRight size={11} />
          </span>
        </div>
      )}
    </Link>
  );
}

export default function AIResearchPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("date");

  const fetchFn = useCallback(
    (page) => fetchAISection("research", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-research",
    getCacheKey: (page) => `ai-research-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((p) => {
      const matchCat = cat === "All" || p.category === cat;
      const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || (p.summary || p.abstract || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sort === "citations") return (b.citations || 0) - (a.citations || 0);
      return new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0);
    });

  const totalCitations = allItems.reduce((s, p) => s + (p.citations || 0), 0);

  return (
    <>
      <SEO
        title="AI Research — Latest Papers, Breakthroughs & Publications · TrendMing"
        description="Track the latest AI research papers from NeurIPS, ICLR, arXiv, and top AI labs. Summaries of breakthroughs in LLMs, reasoning, safety, and more."
        canonical="https://trendming.com/trending-ai/research"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Research</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Research Radar</h1>
          <p className="text-slate-500 text-sm mt-1">Curated research papers from top AI labs and conferences with accessible summaries.</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "Papers Tracked", value: allItems.length },
            { label: "Total Citations", value: totalCitations.toLocaleString() },
            { label: "New This Week", value: allItems.filter((p) => p.isNew || p.is_new).length },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search papers…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            <option value="date">Newest first</option>
            <option value="citations">Most cited</option>
          </select>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {RESEARCH_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((paper) => <PaperCard key={paper.id} paper={paper} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No papers found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load research papers</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}
      </div>
    </>
  );
}
